import {ModelRow} from "@mcleod/core";
import {
    DataSourceMode, Image,
    Label, Layout, SlideoutDecorator, TableCell, TableRow,
    TableRowDisplayEvent,
    TableSelectionEvent
} from "@mcleod/components";
import { FilterPanel } from "@mcleod/dispatch";
import { AutogenLayoutOrderPayablesPendingReview } from "./autogen/AutogenLayoutOrderPayablesPendingReview";
import { OrderPayablesTabLayout } from "./OrderPayables";
import { OrderPayablesUtil } from "./OrderPayablesUtil";
import { RowOrderPayablesProfile } from "./models/ModelOrderPayablesProfile";
import { OrderPayablesTaskFilter } from "./OrderPayablesTaskFilter";
import { SecondaryFilter } from "@mcleod/dispatch/src/planning/FilterPanel";
import { ReadyToSubmitSlideout } from "./ReadyToSubmitSlideout";

interface ColumnVisibilityState {
    showException: boolean;
    showPaymentInfo: boolean;
    showType: boolean;
    showReadyToSubmitButton: boolean;
    showSubmittedToTriumphPay: boolean;
}

export class OrderPayablesPendingReview extends AutogenLayoutOrderPayablesPendingReview implements OrderPayablesTabLayout {
    public getProfileRow: () => RowOrderPayablesProfile;
    public filterPanel: FilterPanel;
    columnManager: ColumnVisibilityManager;
    public getLayoutGroupBy: () => any;
    public getPayablesLookupMap: () => any;

    readonly NO_FILTER_VISIBILITY: ColumnVisibilityState = {
        showException: false,
        showPaymentInfo: false,
        showType: true,
        showReadyToSubmitButton: false,
        showSubmittedToTriumphPay: false
    };

    filterConfigs: { [key: string]: { id: string, getVisibility: (active: boolean) => ColumnVisibilityState } };

    onLoad(): void | Promise<void> {
        this.initializeFilterPanel();
        this.columnManager = new ColumnVisibilityManager(this.tableOrderPayables, this.layoutOrderPayablesToolPanel);
        this.setupRowDisplayListener();
        this.configureFilters();

        this.setInitialColumnVisibility();
    }

    private initializeFilterPanel(): void {
        this.filterPanel = new FilterPanel(
            [OrderPayablesTaskFilter.TP_EXCEPTION,
            OrderPayablesTaskFilter.TP_READY_TO_SUBMIT,
            OrderPayablesTaskFilter.TP_SUBMITTED],
            this.tableOrderPayables,
            this.panelTaskFilters
        );
        this.panelTaskFilters.visible = false;
        this.configureFilters();
    }

    private hasActiveFilters(): boolean {
        return this.filterPanel.filters.some(f => f.active);
    }

    private getVisibilityForDeactivatedFilter(): ColumnVisibilityState {
        if (!this.hasActiveFilters()) {
            return this.NO_FILTER_VISIBILITY;
        }
        const activeFilter = this.filterPanel.getActiveFilter();
        if (activeFilter) {
            const config = this.filterConfigs[activeFilter.id];
            return config ? config.getVisibility(true) : this.NO_FILTER_VISIBILITY;
        }
        return this.NO_FILTER_VISIBILITY;
    }

    private setInitialColumnVisibility(): void {
        this.columnManager.updateVisibility(this.NO_FILTER_VISIBILITY);
    }

    private setupRowDisplayListener(): void {
        this.tableOrderPayables.addRowDisplayListener(this.tableOrderPayablesOnRowDisplay.bind(this));
    }

    private configureFilters(): void {
        this.filterConfigs = {
            exception: {
                id: "exception",
                getVisibility: (active: boolean): ColumnVisibilityState => ({
                    showException: active,
                    showPaymentInfo: false,
                    showType: false,
                    showReadyToSubmitButton: false,
                    showSubmittedToTriumphPay: false
                })
            },
            readyToSubmit: {
                id: "readyToSubmit",
                getVisibility: (active: boolean): ColumnVisibilityState => ({
                    showException: false,
                    showPaymentInfo: false,
                    showType: false,
                    showReadyToSubmitButton: active,
                    showSubmittedToTriumphPay: false
                })
            },
            submittedToTriumphPay: {
                id: "submittedToTriumphPay",
                getVisibility: (active: boolean): ColumnVisibilityState => ({
                    showException: false,
                    showPaymentInfo: true,
                    showType: false,
                    showReadyToSubmitButton: false,
                    showSubmittedToTriumphPay: active
                })
            }
        };

        Object.values(this.filterConfigs).forEach(config => {
            const filters = this.filterPanel.filters.filter(f => f.id === config.id);
            filters.forEach(filter => this.configureFilter(filter, config.getVisibility));
        });
    }

    private configureFilter(filter: SecondaryFilter,
                            getVisibility: (active: boolean) => ColumnVisibilityState): void {
        filter.button.addClickListener(() => {
            let visibility;

            // If this filter is being activated, apply its visibility settings
            if (filter.active) {
                visibility = getVisibility(true);
            } else {
                // If this filter is being deactivated, check for other active filters
                visibility = this.getVisibilityForDeactivatedFilter();
            }

            // Always ensure exception column is only visible when exception filter is active
            const exceptionFilterActive = this.filterPanel.filters.find(f => f.id === "exception")?.active || false;
            visibility.showException = exceptionFilterActive;

            this.columnManager.updateVisibility(visibility);
        });
    }

    tableOrderPayablesOnRowDisplay(event: TableRowDisplayEvent): void {
        const tableRow = event.getTableRow();
        const tableRowData = tableRow.data;
        this.panelTaskFilters.visible = tableRowData?.data?.triumphpay_active;

        tableRow.forEveryChildComponent((component) => {
            OrderPayablesUtil.setTooltipContent(component, tableRow.data);
        });

        OrderPayablesUtil.setComponentDetails({
            tableRow: tableRow,
            profileRow: this.getProfileRow()
        });
    }

    tableOrderPayablesOnSelect(event: TableSelectionEvent): void {
        const selectedRows = this.tableOrderPayables.selectedRows;
        const someFreezePaySelected = selectedRows.some(row => !row.data.isNull("freeze_by"));
        const isReadyToSubmitActive = this.filterPanel.getActiveFilter()?.id === "readyToSubmit";

        const buttonState = {
            enabled: selectedRows.length > 0 && !someFreezePaySelected && isReadyToSubmitActive,
            visible: selectedRows.length > 0 && !someFreezePaySelected && isReadyToSubmitActive
        };

        this.layoutOrderPayablesToolPanel.buttonReadyToSubmit.enabled = buttonState.enabled;

        if (someFreezePaySelected) {
            this.layoutOrderPayablesToolPanel.buttonReadyToSubmit.disabledTooltip = "Cannot process frozen payables";
        }
    }

    openTriumphPaySubmissionSlideout(selectedRowData: ModelRow[]): void {
        const slideoutLayout = Layout.getLayout("lme/settlement/ReadyToSubmitSlideout") as ReadyToSubmitSlideout;
        new SlideoutDecorator({
            layout: slideoutLayout,
            width: window.innerWidth * .75,
            fillVerticalSpace: true,
            title: "Submit To Triumph",
            addlComponents: [slideoutLayout.readyToSubmitButton],
            doAfterSlideIn: async () => {
                this.initializeSlideoutContent(slideoutLayout, selectedRowData);
            }
        });
    }

    private initializeSlideoutContent(slideoutLayout: ReadyToSubmitSlideout, selectedRowData: ModelRow[]): void {
        const submittalTable = slideoutLayout.layoutSubmittalSettlements
        const table = slideoutLayout.layoutSubmittalSettlements.tableReadyToSubmitOrderPayables;

        // Get the parent OrderPayables layout to access its data
        const orderPayablesLayout = this.getParentLayout() as any; // Cast to any to access private properties

        // Pass the actual data/getters to the SubmittalSettlementsTable layout
        submittalTable.getLayoutGroupBy = () => orderPayablesLayout?.layoutGroupBy;
        submittalTable.getPayablesLookupMap = () => orderPayablesLayout?.payablesLookupMap;

        table.dataSource.setRowsAndMode(DataSourceMode.NONE, selectedRowData);
        table.selectedRows = table.rows;
        slideoutLayout.layoutSubmittalSettlements.labelSubmitCount.text =
            `Selected Settlements (${selectedRowData.length})`;
    }

    getLayoutBaseFilter(row: ModelRow): boolean {
        return row.get("ready_to_pay_flag") == "N" && row.isNull("back_office_reason_code");
    }
}

class ColumnVisibilityManager {
    constructor(
        private readonly table: any,
        private readonly toolPanel: any
    ) { }

    updateVisibility(state: ColumnVisibilityState): void {
        const hasActiveFilter = state.showException || state.showReadyToSubmitButton || state.showSubmittedToTriumphPay;
        this.updateColumnVisibility("Exception", state.showException);
        this.updateColumnVisibility("Type", !hasActiveFilter);
        this.updateColumnVisibility("columnHeaderPaymentId", (state.showPaymentInfo));
        this.updateColumnVisibility("columnHeaderPaymentStatus", state.showPaymentInfo);
        this.updateColumnVisibilityById("columnHeaderWarning", !state.showSubmittedToTriumphPay);

        this.updateCellsVisibility(state.showException, state.showPaymentInfo, state.showSubmittedToTriumphPay, hasActiveFilter);
        this.updateReadyToSubmitButton(state.showReadyToSubmitButton);
    }

    private updateColumnVisibilityById(headingId: string, visible: boolean): void {
        const column = this.table.columns.find(col => col.heading?.id === headingId);
        if (column) {
            this.table.setColumnVisibility(column, visible);
        }
    }

    private updateColumnVisibility(caption: string, visible: boolean): void {
        const column = this.table.getColumnByCaption(caption);
        if (column) {
            this.table.setColumnVisibility(column, visible);
        }

    }

    private updateCellsVisibility(showException: boolean, showPaymentInfo: boolean, showSubmittedToTriumphPay: boolean, hasActiveFilter: boolean): void {
        this.table.rows.forEach(row => {
            this.updateCell(row, "tableOrderPayablesExceptionColumn", Label, showException);
            this.updateCell(row, "paymentIdCell", Label, (showPaymentInfo));
            this.updateCell(row, "paymentStatusCell", Label, showPaymentInfo);
            this.updateCell(row, "tableOrderPayablesTypeColumn", Image, !hasActiveFilter);

            // Handle warning cell and image separately
            const cell = row.findComponentById("adjustmentWarningCell") as TableCell;
            if (cell) {
                const hasPayAdjustment = row.data?.get("pay_adjustment") === "Y";
                const shouldShowWarning = !showSubmittedToTriumphPay && hasPayAdjustment;

                // Only show cell when there's an actual warning to display
                cell.visible = shouldShowWarning;

                const warningImage = cell.components.find(c => c instanceof Image);
                if (warningImage) {
                    warningImage.visible = shouldShowWarning;
                }
            }
        });
    }

    private updateCell(row: TableRow, cellId: string, componentType: any, isVisible: boolean): void {
        let cell = row.findComponentById(cellId) as TableCell;
        if (!cell) {
            cell = row.cells.find(c => c.id === cellId);
        }
        if (cell) {
            cell.visible = isVisible;
            // Update all components in the cell instead of just one type
            cell.components.forEach(component => {
                component.visible = isVisible;
            });
        }
    }

    private updateReadyToSubmitButton(visible: boolean): void {
        if (this.toolPanel?.buttonReadyToSubmit) {
            this.toolPanel.buttonReadyToSubmit.visible = visible;
        }
    }
}
